import React, { useContext, useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";
import AddNewEmail from "../ProjectDetails/addNewEmail";
import DragAndDrop from "../SharedProfiles/drag&drop";

export default function EmailForm({
  projectID,
  type,
  language,
  onClose,
  onSend,
  initialContent,
  userData,
  applicantName,
  applicantEmail,
}) {
  const { API_URL } = useContext(UrlContext);
  const [htmlContentToDisplay, setHtmlContentToDisplay] = useState("");
  const divRef = useRef(null);
  const [mailVersion, setMailVersion] = useState(language);
  const [hasAttachment, setHasAttachment] = useState(false);
  const [openDragDrop, setOpenDragDrop] = useState(false);
  const [hasBcc, setHasBcc] = useState(false);
  const [sendTo, setSendTo] = useState([applicantEmail]);
  const [ccMail, setCcMail] = useState(null);
  const [bccMail, setBccMail] = useState([]);
  const [mailContent, setMailContent] = useState({
    subject: `expertpowerhouse | ${
      type === "accept" ? "Application Accepted" : "Application Rejected"
    }`,
    body: "",
  });
  const [base64Strings, setBase64Strings] = useState([]);

  useEffect(() => {
    setMailVersion(language);
    setHasAttachment(false);
    setOpenDragDrop(false);
    setHasBcc(false);
    setSendTo([applicantEmail]);
    setCcMail(null);
    setBccMail([]);
    setBase64Strings([]);

    setMailContent({
      subject: `expertpowerhouse | ${
        type === "accept" ? "Application Accepted" : "Application Rejected"
      }`,
      body: getDefaultContent(language, type, userData.username, applicantName),
    });
  }, [type, language, userData.username, applicantName, applicantEmail]);

  function getDivInnerHTML() {
    const div = document.querySelector(".editable-div-for-email-body");
    return div.innerHTML;
  }

  const handleMailVersion = (e) => {
    const newVersion = e.target.value;
    setMailVersion(newVersion);
    setMailContent((prevContent) => ({
      ...prevContent,
      body: getDefaultContent(
        newVersion,
        type,
        userData.username,
        applicantName
      ),
    }));
  };

  const handleSubjectChange = (e) => {
    setMailContent({
      ...mailContent,
      subject: e.target.value,
    });
  };

  const handleSendToChange = (attendees) => {
    setSendTo(attendees);
  };

  const handleCcMailChange = (attendees) => {
    setCcMail(attendees);
  };

  const handleBccMailChange = (attendees) => {
    setBccMail(attendees);
  };

  function handleRemoveFile(index) {
    const updatedBase64Strings = [...base64Strings];
    updatedBase64Strings.splice(index, 1);
    setBase64Strings(updatedBase64Strings);
    if (updatedBase64Strings.length === 0) {
      setHasAttachment(false);
    }
  }

  const getDefaultContent = (language, type, senderName, recipientName) => {
    const buttonHtml = `
      <table cellspacing="0" cellpadding="0" style="margin: 0 auto;">
        <tr>
          <td align="center" style="border-radius: 5px;">
            <a href="http://connect.expertpowerhouse.com/" 
               target="_blank" 
               style="font-size: 16px;  background-color:#365561; font-family: Helvetica, Arial, sans-serif; color: #ffffff; text-decoration: none; padding: 10px 20px; border: 1px solid #365561; display: inline-block; white-space: nowrap;">
              Log In
            </a>
          </td>
        </tr>
      </table>
    `;

    if (type === "accept") {
      return language === "EN"
        ? `<p>Dear ${recipientName.split(" ")[0]},</p>

<p>Thank you very much for applying to our network. We are happy to tell you that your application has been reviewed and is successful.</p>
<p>Welcome to expertpowerhouse!</p>
<p>To complete your profile please log in to your account at:</p>
${buttonHtml}
<p>You can use your LinkedIn credentials or request a new password for a first-time login.</p>
<p>We are looking forward to collaborating soon and will reach out with matching project opportunities as they come up.</p>
<p>Best regards</p>
${`<p>${senderName}</p>`}`
        : `<p>Hi ${recipientName.split(" ")[0]},</p>

<p>vielen Dank für deine Bewerbung in unserem Netzwerk. Wir freuen uns, dir mitteilen zu können, dass deine Bewerbung geprüft wurde und erfolgreich ist.</p>
<p>Willkommen bei expertpowerhouse!</p>

<p>Um dein Profil zu vervollständigen, melde dich bitte unter folgendem Link in deinem Konto an:</p>
${buttonHtml}
<p>Das Profil ist bereits mit deinen LinkedIn Daten vorbefüllt, du kannst aber flexibel Änderungen vornehmen. Du kannst deine LinkedIn-Anmeldedaten verwenden oder ein neues Passwort für die erstmalige Anmeldung anfordern. Wir freuen uns auf die baldige Zusammenarbeit und werden uns mit passenden Projektmöglichkeiten bei dir melden, sobald diese verfügbar sind.</p>

<p>Viele Grüße</p>
${`<p>${senderName}</p>`}`;
    } else {
      return language === "EN"
        ? `<p>Dear ${recipientName.split(" ")[0]},</p>

<p>Thank you very much for your application to become a member of the expertpowerhouse network.</p>
<p>Your application has been reviewed and we have to regrettably tell you that we cannot consider it at this point in time as we do not have enough business in your area of expertise.</p>
<p>We wish you all the best for your future and hope that our paths cross again at a later stage.</p>

<p>Best regards</p>
${`<p>${senderName}</p>`}`
        : `<p>Sehr geehrter ${recipientName.split(" ")[0]},</p>

<p>vielen Dank für Ihre Bewerbung als Mitglied des expertpowerhouse-Netzwerks.</p>
<p>Ihre Bewerbung wurde geprüft und wir müssen Ihnen leider mitteilen, dass wir sie zum jetzigen Zeitpunkt nicht berücksichtigen können, da wir nicht genügend Projekte in Ihrem Fachgebiet haben.</p>
<p>Wir wünschen Ihnen alles Gute für Ihre Zukunft und hoffen, dass sich unsere Wege zu einem späteren Zeitpunkt wieder kreuzen werden.</p>

<p>Mit freundlichen Grüßen</p>
${`<p>${senderName}</p>`}`;
    }
  };

  const handleSendEmail = () => {
    let displayMessage = `<p><b>Sending to:</b> ${sendTo}</p>`;
    if (ccMail && ccMail.length > 0 && bccMail.length === 0) {
      displayMessage = `<p><b>Sending to:</b> ${sendTo}</p><p><b>Cc:</b> ${ccMail}</p>`;
    } else if (ccMail && ccMail.length > 0 && bccMail.length > 0) {
      displayMessage = `<p><b>Sending to:</b> ${sendTo}</p><p><b>Cc:</b> ${ccMail}</p><p><b>Bcc:</b> ${bccMail}</p>`;
    }
    Swal.fire({
      title: `${
        type == "accept"
          ? "Accept user and send email?"
          : "Reject user and send email?"
      }`,
      icon: "warning",
      html: displayMessage,
      showCancelButton: true,
      confirmButtonColor: "#365561",
      cancelButtonColor: "#d33",
      confirmButtonText: "Send",
    }).then((result) => {
      if (result.isConfirmed) {
        const emailContent = {
          projectID: projectID,
          accessToken: localStorage.getItem("accesstoken"),
          sendTo: sendTo,
          ccMail: ccMail,
          bccMail: bccMail,
          subject: mailContent.subject,
          body: getDivInnerHTML(),
          signature: "",
          attachment: base64Strings,
          from: userData.email,
          fromName: userData.username,
          enableSignature: false,
        };
        onSend(emailContent);
      }
    });
  };

  return (
    <>
      <div className="decline-wrapper">
        <div
          className="flex-container-decline-profile"
          style={{
            width: "98%",
            height: "73%",
            padding: "0px",
            border: "none",
          }}
        >
          <div
            className="flex-item-decline-profile-header"
            style={{ flex: "2%", background: "#365561" }}
          >
            <div style={{ display: "flex", flexDirection: "row" }}>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "white",
                  marginLeft: "5px",
                }}
              >
                Email to client |{" "}
              </p>
              <select
                style={{
                  outline: "none",
                  border: "none",
                  background: "none",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
                value={mailVersion}
                onChange={handleMailVersion}
              >
                <option style={{ color: "black" }}>EN</option>
                <option style={{ color: "black" }}>DE</option>
              </select>
            </div>
            <i
              className="fa-solid fa-xmark"
              style={{
                marginRight: "5px",
                marginTop: "3px",
                color: "gray",
                cursor: "pointer",
              }}
              onClick={onClose}
            />
          </div>
          <div
            className="flex-item-sugest-time-slot"
            style={{ flex: "98%", marginTop: "10px" }}
          >
            <div
              className="flex-container-emil"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <div
                className="flex-item-email-action"
                style={{ flex: "20%", borderBottom: "1px solid lightgrey" }}
              >
                <div
                  className="flex-container-email-action-container"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: "10px",
                    gap: "10px",
                    padding: "5px",
                  }}
                >
                  <div
                    className="flex-item-email-action-container-item-send"
                    style={{
                      flex: "5%",
                      borderRadius: "5px",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                    }}
                    onClick={handleSendEmail}
                  >
                    <i
                      className="fa-solid fa-paper-plane"
                      style={{
                        marginTop: "15%",
                        fontSize: "30px",
                        color: sendTo ? "#365561" : "gray",
                      }}
                    ></i>
                    <span style={{ marginBottom: "10%" }}>Send</span>
                  </div>
                  <div
                    className="flex-item-email-action-container-item-inputs"
                    style={{
                      flex: "90%",
                      display: "flex",
                      flexDirection: "column",
                      marginBottom: "10px",
                      gap: "10px",
                    }}
                  >
                    <div
                      className="input-attendees-subject"
                      style={{ outline: "none", height: "36px" }}
                    >
                      {sendTo && (
                        <AddNewEmail
                          handleInputChange={handleSendToChange}
                          currentEmails={sendTo}
                        />
                      )}
                    </div>
                    <div
                      className="input-attendees-subject"
                      style={{ outline: "none", height: "36px" }}
                    >
                      {ccMail && (
                        <AddNewEmail
                          handleInputChange={handleCcMailChange}
                          currentEmails={ccMail}
                        />
                      )}
                    </div>
                    {hasBcc && (
                      <div
                        className="input-attendees-subject"
                        style={{ outline: "none", height: "36px" }}
                      >
                        {bccMail && (
                          <AddNewEmail
                            handleInputChange={handleBccMailChange}
                            currentEmails={bccMail}
                          />
                        )}
                      </div>
                    )}
                    <input
                      className="input-attendees-subject"
                      value={mailContent.subject}
                      onChange={handleSubjectChange}
                      placeholder="Subject"
                      style={{
                        outline: "none",
                        height: "35px",
                        width: "100%",
                        padding: "5px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div
                className="flex-item-email-body"
                style={{ flex: "80%", marginTop: "10px" }}
              >
                <div
                  className="editable-div-for-email-body"
                  contentEditable="true"
                  dangerouslySetInnerHTML={{ __html: mailContent.body }}
                  style={{
                    height: "100%",
                    overflowY: "auto",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {openDragDrop && (
        <DragAndDrop
          setOpenDragDrop={setOpenDragDrop}
          setBase64Strings={setBase64Strings}
          setHasAttachment={setHasAttachment}
        />
      )}
    </>
  );
}
